/**
 * <lainly-ugc>: the customer photo row. It glides sideways without stopping (scrolled a little each frame,
 * looping back by one row's width; only while on screen, and not with reduced motion).
 * Tapping a photo opens it large in the section's <dialog>,
 * with arrows (and arrow keys or a sideways swipe) to move through the photos and a close button.
 * Both copies of the looping row open the same photo, by its index in data-ugc-open.
 * Switching is instant: the viewer first shows the row's already-loaded photo, swaps in the sharp one
 * once it is ready, and loads the other photos ahead of time.
 */
if (!customElements.get('lainly-ugc')) {
  customElements.define(
    'lainly-ugc',
    class LainlyUgc extends HTMLElement {
      connectedCallback() {
        this.#glide();
        this.viewer = this.querySelector('[data-ugc-viewer]');
        if (!this.viewer) return;

        this.image = this.viewer.querySelector('[data-ugc-image]');
        this.caption = this.viewer.querySelector('[data-ugc-caption]');
        this.photos = [...this.querySelector('.lainly-ugc__row:not(.lainly-ugc__row--copy)').querySelectorAll('[data-ugc-open]')];
        this.index = 0;
        /** Sharp photo URLs already downloaded and decoded, mapped to their loading promise. */
        this.ready = new Map();
        this.controller = new AbortController();
        const { signal } = this.controller;

        this.addEventListener('click', (event) => this.#onClick(event), { signal });
        this.viewer.addEventListener('close', () => this.#unlock(), { signal });
        this.viewer.addEventListener('keydown', (event) => this.#onKey(event), { signal });
        this.viewer.addEventListener('touchstart', (event) => (this.touchX = event.touches[0].clientX), {
          passive: true,
          signal,
        });
        this.viewer.addEventListener('touchend', (event) => this.#onSwipe(event), { passive: true, signal });
      }

      disconnectedCallback() {
        this.controller?.abort();
        this.visibility?.disconnect();
        this.sizes?.disconnect();
        cancelAnimationFrame(this.frame);
        this.#unlock();
      }

      /** Speed in pixels per second: a little quicker on phones, where the photos are narrower. */
      static SPEED_PHONE = 125;
      static SPEED_DESKTOP = 100;

      /**
       * Moves the row along by scrolling its window, so only what is on screen is drawn. The position is kept
       * as a fraction (scrollLeft rounds) and wraps back by one row's width for a seamless loop.
       */
      #glide() {
        const viewport = this.querySelector('.lainly-ugc__viewport');
        const row = this.querySelector('.lainly-ugc__row');
        if (!(viewport instanceof HTMLElement) || !(row instanceof HTMLElement)) return;
        if (matchMedia('(prefers-reduced-motion: reduce)').matches) return;

        let position = viewport.scrollLeft;
        let last = 0;
        // The row's width and the speed are kept up to date by observers instead of being measured every frame,
        // so each frame only moves the row (measuring forces the phone to redo the page layout 60 times a second).
        let width = row.offsetWidth;
        this.sizes = new ResizeObserver(() => (width = row.offsetWidth));
        this.sizes.observe(row);
        const desktop = matchMedia('(min-width: 990px)');
        const step = (now) => {
          const elapsed = last ? Math.min(now - last, 100) : 0;
          last = now;
          if (width > 0 && !this.viewer?.open) {
            const speed = desktop.matches ? LainlyUgc.SPEED_DESKTOP : LainlyUgc.SPEED_PHONE;
            position = (position + (speed * elapsed) / 1000) % width;
            viewport.scrollLeft = position;
          }
          this.frame = requestAnimationFrame(step);
        };
        // Only while the row is on screen.
        this.visibility = new IntersectionObserver(([entry]) => {
          cancelAnimationFrame(this.frame);
          last = 0;
          if (entry?.isIntersecting) this.frame = requestAnimationFrame(step);
        });
        this.visibility.observe(viewport);
      }

      #onClick(event) {
        const opener = event.target.closest('[data-ugc-open]');
        if (opener) return this.open(Number(opener.dataset.ugcOpen));

        const step = event.target.closest('[data-ugc-step]');
        if (step) return this.show(this.index + Number(step.dataset.ugcStep));

        // The close button, or a tap on the dark backdrop around the photo.
        if (event.target.closest('[data-ugc-close]') || event.target === this.viewer) this.close();
      }

      #onKey(event) {
        if (event.key === 'ArrowRight') this.show(this.index + 1);
        if (event.key === 'ArrowLeft') this.show(this.index - 1);
      }

      #onSwipe(event) {
        if (this.touchX == null) return;
        const distance = event.changedTouches[0].clientX - this.touchX;
        this.touchX = null;
        if (Math.abs(distance) > 40) this.show(this.index + (distance < 0 ? 1 : -1));
      }

      open(index) {
        this.show(index);
        if (this.viewer.open) return;
        this.viewer.showModal();
        document.documentElement.style.overflow = 'hidden';
        // Fetch the rest in the background, so even quick taps through the arrows never wait.
        this.photos.forEach((photo) => this.#load(photo.dataset.ugcFull));
      }

      /** Shows the photo at `index`, wrapping around at both ends. */
      show(index) {
        const count = this.photos.length;
        if (!count) return;
        this.index = (index + count) % count;

        const photo = this.photos[this.index];
        const full = photo.dataset.ugcFull;
        const shown = this.index;

        // Show the sharp photo right away when it is ready; otherwise the row's copy, then swap.
        if (this.ready.get(full)?.done) {
          this.image.src = full;
        } else {
          const thumb = photo.querySelector('img');
          this.image.src = thumb?.currentSrc || thumb?.src || full;
          this.#load(full).then(() => {
            if (this.index === shown) this.image.src = full;
          });
        }
        this.image.alt = photo.dataset.ugcAlt || '';
        if (this.caption) {
          this.caption.textContent = photo.dataset.ugcName || '';
          this.caption.hidden = !photo.dataset.ugcName;
        }

        this.#load(this.photos[(this.index + 1) % count].dataset.ugcFull);
        this.#load(this.photos[(this.index - 1 + count) % count].dataset.ugcFull);
      }

      /** Downloads and decodes a photo once, so showing it later is instant. */
      #load(url) {
        if (!this.ready.has(url)) {
          const entry = { done: false };
          const image = new Image();
          image.src = url;
          entry.promise = image
            .decode()
            .catch(() => {})
            .then(() => (entry.done = true));
          this.ready.set(url, entry);
        }
        return this.ready.get(url).promise;
      }

      close() {
        if (this.viewer.open) this.viewer.close();
        this.#unlock();
      }

      #unlock() {
        document.documentElement.style.removeProperty('overflow');
      }
    }
  );
}
